import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import { deleteTalentProfile } from '../../../services/talentService';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const TalentTable = ({ talents, onEdit, onRefresh }) => {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (talentId, talentName) => {
    if (!window.confirm(`Are you sure you want to delete ${talentName}? This action cannot be undone.`)) {
      return;
    }

    setDeleting(talentId);
    
    try {
      const { error } = await deleteTalentProfile(talentId);
      
      if (error) {
        alert(`Error deleting talent: ${error}`);
      } else {
        onRefresh?.();
      }
    } catch (err) {
      console.error('Error deleting talent:', err);
      alert('Failed to delete talent. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const handleViewDetails = (talentId) => {
    navigate(`/add-edit-talent-profile/${talentId}`);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString)?.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const formatSocialMedia = (username) => {
    if (!username) return '-';
    return username?.startsWith('@') ? username : `@${username}`;
  };

  // Get public URL for headshot from Supabase storage
  const getHeadshotUrl = (headshotPath) => {
    if (!headshotPath) return null;
    
    const { data } = supabase?.storage?.from('profile-images')?.getPublicUrl(headshotPath);
    
    return data?.publicUrl;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-white bg-opacity-5 border-b border-white border-opacity-20">
          <tr>
            <th className="text-left py-4 px-6 font-semibold text-white">Name</th>
            <th className="text-left py-4 px-6 font-semibold text-white">Videos</th>
            <th className="text-left py-4 px-6 font-semibold text-white">Roles</th>
            <th className="text-left py-4 px-6 font-semibold text-white">Contact</th>
            <th className="text-left py-4 px-6 font-semibold text-white">Social Media</th>
            <th className="text-left py-4 px-6 font-semibold text-white">Created</th>
            <th className="text-right py-4 px-6 font-semibold text-white">Actions</th>
          </tr>
        </thead>
        <tbody>
          {talents?.map((talent) => (
            <tr key={talent?.id} className="border-b border-white border-opacity-10 hover:bg-white hover:bg-opacity-5 transition-colors">
              <td className="py-4 px-6">
                <div className="flex items-center space-x-3">
                  {/* Talent Photo Icon */}
                  <div className="flex-shrink-0">
                    {talent?.headshot_url ? (
                      <img
                        src={getHeadshotUrl(talent?.headshot_url)}
                        alt={`${talent?.first_name} ${talent?.last_name} headshot`}
                        className="w-10 h-10 rounded-full object-cover border-2 border-white border-opacity-20"
                        onError={(e) => {
                          // Fallback to icon if image fails to load
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center ${talent?.headshot_url ? 'hidden' : 'flex'}`}
                      style={{ display: talent?.headshot_url ? 'none' : 'flex' }}
                    >
                      <Icon name="User" size={20} className="text-white" />
                    </div>
                  </div>

                  {/* Name and Display Name */}
                  <div>
                    <button
                      onClick={() => handleViewDetails(talent?.id)}
                      className="font-medium text-white hover:text-blue-200 transition-colors cursor-pointer text-left"
                    >
                      {talent?.first_name} {talent?.last_name}
                    </button>
                    {talent?.display_name && (
                      <div className="text-sm text-blue-200">
                        "{talent?.display_name}"
                      </div>
                    )}
                  </div>
                </div>
              </td>

              <td className="py-4 px-6">
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    <Icon name="Video" size={16} className="text-blue-200" />
                    <span className="text-white font-medium">
                      {talent?.video_count || 0}
                    </span>
                  </div>
                  {(talent?.video_count || 0) > 0 ? (
                    <span className="text-xs px-2 py-1 bg-green-500 bg-opacity-20 text-green-200 rounded-full">
                      Assigned
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 bg-orange-500 bg-opacity-20 text-orange-200 rounded-full">
                      Unassigned
                    </span>
                  )}
                </div>
              </td>

              <td className="py-4 px-6">
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    <Icon name="User" size={16} className="text-purple-200" />
                    <span className="text-white font-medium">
                      {talent?.role_count || 0}
                    </span>
                  </div>
                  {(talent?.role_count || 0) > 0 ? (
                    <span className="text-xs px-2 py-1 bg-purple-500 bg-opacity-20 text-purple-200 rounded-full">
                      {talent?.role_count === 1 ? '1 role' : `${talent?.role_count} roles`}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 bg-gray-500 bg-opacity-20 text-gray-200 rounded-full">
                      No roles
                    </span>
                  )}
                </div>
              </td>
              
              <td className="py-4 px-6">
                <div className="space-y-1">
                  {talent?.email && (
                    <div className="text-sm text-white flex items-center space-x-1">
                      <Icon name="Mail" size={14} />
                      <span>{talent?.email}</span>
                    </div>
                  )}
                  {talent?.phone && (
                    <div className="text-sm text-blue-200 flex items-center space-x-1">
                      <Icon name="Phone" size={14} />
                      <span>{talent?.phone}</span>
                    </div>
                  )}
                  {!talent?.email && !talent?.phone && (
                    <span className="text-sm text-blue-200">No contact info</span>
                  )}
                </div>
              </td>

              <td className="py-4 px-6">
                <div className="space-y-1">
                  {talent?.instagram && (
                    <div className="text-sm text-white flex items-center space-x-1">
                      <Icon name="Instagram" size={14} />
                      <span>{formatSocialMedia(talent?.instagram)}</span>
                    </div>
                  )}
                  {talent?.youtube && (
                    <div className="text-sm text-blue-200 flex items-center space-x-1">
                      <Icon name="Youtube" size={14} />
                      <span>{formatSocialMedia(talent?.youtube)}</span>
                    </div>
                  )}
                  {talent?.tiktok && (
                    <div className="text-sm text-blue-200 flex items-center space-x-1">
                      <Icon name="Video" size={14} />
                      <span>{formatSocialMedia(talent?.tiktok)}</span>
                    </div>
                  )}
                  {!talent?.instagram && !talent?.youtube && !talent?.tiktok && (
                    <span className="text-sm text-blue-200">No social media</span>
                  )}
                </div>
              </td>

              <td className="py-4 px-6">
                <div className="text-sm text-blue-200">
                  {formatDate(talent?.created_at)}
                </div>
              </td>

              <td className="py-4 px-6">
                <div className="flex items-center justify-end space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDetails(talent?.id)}
                    className="flex items-center space-x-1 border-white text-white hover:bg-white hover:text-indigo-900"
                  >
                    <Icon name="Eye" size={14} />
                    <span>View</span>
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit?.(talent?.id)}
                    className="flex items-center space-x-1 border-blue-200 text-blue-200 hover:bg-blue-200 hover:text-indigo-900"
                  >
                    <Icon name="Edit" size={14} />
                    <span>Edit</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(talent?.id, `${talent?.first_name} ${talent?.last_name}`)}
                    disabled={deleting === talent?.id}
                    className="flex items-center space-x-1 border-red-300 text-red-300 hover:bg-red-300 hover:text-indigo-900"
                  >
                    <Icon name="Trash2" size={14} />
                    <span>{deleting === talent?.id ? 'Deleting...' : 'Delete'}</span>
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TalentTable;